import { useState } from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { WM_EXPORT_TIMEOUT_MS, blobErrorMessage } from "@/components/waste-meals/wmUtils";

const G = OPS.wm.grid;
const TYPES = ["waste", "meals", "all"];

/** A row of pill choices (the module's chip selector, `CoBranchSelect.ChipRow` look). */
function ChoiceRow({ label, value, options, onChange, testId, optionTestId, disabled }) {
    return (
        <div data-testid={testId} className="space-y-1.5">
            <p className="text-xs font-bold text-[color:var(--ink-muted)]">{label}</p>
            <div className="flex flex-wrap gap-1.5">
                {options.map(([k, text]) => (
                    <button key={k} type="button" data-testid={optionTestId(k)} data-active={value === k ? "true" : "false"}
                            disabled={disabled} onClick={() => onChange(k)}
                            className={`h-9 max-w-full truncate rounded-full border px-3.5 text-xs font-semibold transition-colors duration-150 disabled:opacity-60 ${
                                value === k
                                    ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange)] text-white"
                                    : "border-[color:var(--border-soft)] text-[color:var(--ink-muted)] hover:bg-[color:var(--brand-orange-soft)] hover:text-[color:var(--ink)]"
                            }`}>
                        {text}
                    </button>
                ))}
            </div>
        </div>
    );
}

/**
 * «تصدير Excel» (A3) — the month's workbook (summary · waste · staff meals ·
 * returns · ledger). A blob download like the stock count's Board export; a
 * refused export carries its JSON error inside the blob. «هذا الفرع / كل
 * فروعي» is offered only to a user with more than one branch.
 */
export function WmExportButton({ scope, branchId, month, type }) {
    const { t, lang } = useI18n();
    const [open, setOpen] = useState(false);
    const [which, setWhich] = useState("branch");
    const [kind, setKind] = useState(type || "all");
    const [busy, setBusy] = useState(false);
    const branches = scope?.branches || [];
    const many = branches.length > 1;
    const here = branches.find((b) => b.id === branchId);
    const hereName = here ? (lang === "ar" ? here.name_ar || here.name_en : here.name_en || here.name_ar) : "";

    const openDialog = () => { setKind(type || "all"); setWhich("branch"); setOpen(true); };

    const run = async () => {
        setBusy(true);
        try {
            const r = await api.get("/waste-meals/export", {
                params: { month, type: kind, branch_id: many && which === "all" ? "all" : branchId, lang },
                responseType: "blob", timeout: WM_EXPORT_TIMEOUT_MS,
            });
            const name = (r.headers?.["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1]
                || `waste_meals_${month}.xlsx`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
            toast.success(t("wm_exported"));
            setOpen(false);
        } catch (e) {
            toast.error((await blobErrorMessage(e, lang)) || t("wm_export_failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button type="button" variant="outline" data-testid={G.exportBtn} onClick={openDialog}
                    disabled={!month || !branchId} className="h-9 gap-1.5 text-xs font-semibold">
                <FileSpreadsheet className="h-4 w-4 text-[color:var(--brand-brown)]" />
                {t("wm_export")}
            </Button>
            <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
                <DialogContent data-testid={G.exportDialog} className="max-w-sm">
                    <DialogHeader className="text-start sm:text-start">
                        <DialogTitle className="text-base">{t("wm_export_title")}</DialogTitle>
                        <DialogDescription className="text-xs">
                            <bdi dir="ltr">{month}</bdi> · {t("wm_export_hint")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {many ? (
                            <ChoiceRow label={t("wm_export_scope")} value={which} onChange={setWhich} disabled={busy}
                                       testId={G.exportScope} optionTestId={G.exportScopeOption}
                                       options={[
                                           ["branch", hereName ? `${t("wm_export_scope_branch")} · ${hereName}` : t("wm_export_scope_branch")],
                                           ["all", `${t("wm_export_scope_all")} (${new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(branches.length)})`],
                                       ]} />
                        ) : null}
                        <ChoiceRow label={t("wm_export_type")} value={kind} onChange={setKind} disabled={busy}
                                   optionTestId={G.exportType}
                                   options={TYPES.map((k) => [k, t(`wm_export_type_${k}`)])} />
                        {/* never a raw net-sales SAR column: the file carries cost and waste % only */}
                        <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">
                            {t("wm_export_cost_note")}
                        </p>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>{t("wm_cancel")}</Button>
                        <Button data-testid={G.exportRun} onClick={run} disabled={busy} className="gap-1.5">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            {busy ? t("wm_exporting") : t("wm_export_download")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
